import { useParams, Link } from 'react-router-dom';
import { getComicById } from '@/mocks/comics';
import { categoryMap } from '@/mocks/categories';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-6">
          <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
        </div>
        <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">漫画未找到</h2>
        <p className="text-foreground-500 text-sm mb-6">抱歉，您要找的漫画不存在或已下架</p>
        <Link to="/" className="px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
          返回首页
        </Link>
      </div>
    );
  }

  const categoryName = categoryMap[comic.category] || comic.category;

  return (
    <>
      <SchemaOrg
        type="Article"
        articleData={{
          headline: `${comic.title} - 韩漫之家`,
          description: comic.description,
          datePublished: comic.chapters[0]?.date || comic.updatedAt,
          dateModified: comic.updatedAt,
          authorName: comic.author,
          imageUrl: comic.cover,
        }}
      />
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: categoryName, href: `/category/${comic.category}` },
          { label: comic.title },
        ]}
      />

      <main className="max-w-4xl mx-auto px-4 md:px-6 pb-14">
        <article>
          <div className="flex flex-col md:flex-row gap-8 mb-10">
            <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[260px]">
              <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩漫之家`}
                  title={`${comic.title} - 韩国漫画在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-600 mb-4">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <Link
                  to={`/category/${comic.category}`}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors"
                >
                  {categoryName}
                </Link>
              </div>

              <div className="flex items-center gap-4 mb-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-yellow-500"></i>
                  {comic.rating}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {(comic.views / 10000).toFixed(1)}万次阅读
                </span>
              </div>

              <div className="bg-background-100 rounded-lg p-4 mb-6">
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button className="flex-1 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  开始阅读
                </button>
                <button className="flex-1 px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  加入书架
                </button>
              </div>
            </div>
          </div>

          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">共 {comic.chapters.length} 话</span>
            </h2>
            <div className="border border-background-200/70 rounded-lg divide-y divide-background-200/70">
              {comic.chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 text-xs font-medium">
                      {chapter.id}
                    </span>
                    <span className="text-sm text-foreground-900 font-medium">{chapter.title}</span>
                  </div>
                  <span className="text-xs text-foreground-400">{chapter.date}</span>
                </div>
              ))}
            </div>
          </section>
        </article>
      </main>
    </>
  );
}